import { useEffect, useState } from "react";
import { FaCamera } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Api from "../service/http";

const ProfileEdit = () => {
  const { theme } = useSelector((state) => state.theme);
  const [user, setUser] = useState(null);
  const [preview, setPreview] = useState(null); // local image preview
  const [file, setFile] = useState(null);       // uploaded file
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // load profile
  const getProfile = async () => {
    const res = await Api.get("/web/profile");
    if (res?.data?.user) {
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser({
        ...res.data.user,
        // coerce to booleans for controlled checkboxes
        push_notification: !!res.data.user?.push_notification,
        email_notification: !!res.data.user?.email_notification,
      });
      setPreview(res.data.user?.dp || null);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUser((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    setErrors({});

    try {
      const formData = new FormData();
      formData.append("first_name", user.first_name ?? "");
      formData.append("phone", user.phone ?? "");
      formData.append("about", user.about ?? "");
      formData.append("push_notification", user.push_notification ? 1 : 0);
      formData.append("email_notification", user.email_notification ? 1 : 0);
      if (file) formData.append("profile_image", file);

      const res = await Api.post("/web/update-profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
        window.location.href = "/profile";
      } else {
        setErrors(res?.data?.errors || {});
      }
    } catch (err) {
      setErrors({ form: ["Update failed. Please try again."] });
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    localStorage.clear();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    window.location.href = '/';
    setLoading(false);
    return <Navigate to="/signin" replace />;
  };

  const unsubscribe = async () => {
    setLoading(true);
    const res = await Api.post(`/web/cancel-subscription-request`);
    if (res?.data?.url) {
      window.location.href = res?.data?.url;
    }
    setLoading(false);
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className={`animate-pulse ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading profile…</div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col h-full overflow-y-auto scrollbar-hide ${theme === 'dark' ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-900'}`}>
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Edit Profile</h1>
            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
              Update your personal information and preferences
            </p>
          </div>
          <button
            onClick={() => window.location.href = "/profile"}
            className={`px-8 py-3 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-lg hover:-translate-y-0.5 active:translate-y-0 ${
              theme === 'dark' 
                ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 shadow-slate-900/40' 
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 shadow-slate-200'
            }`}
          >
            Cancel
          </button>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* Avatar Section */}
          <div className={`${theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2.5rem] p-8 shadow-2xl border flex flex-col items-center`}>
            <div className="relative group">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000" />
              <div 
                className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl cursor-pointer"
                onClick={() => document.getElementById("dpInput").click()}
              >
                <img
                  src={preview || "/placeholder-avatar.png"}
                  alt="Profile"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <FaCamera className="text-white text-3xl" />
                </div>
              </div>
              <button
                type="button"
                className="absolute bottom-2 right-2 h-10 w-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-xl hover:bg-blue-700 hover:scale-110 transition-all duration-300 border-4 border-white dark:border-slate-800"
                onClick={() => document.getElementById("dpInput").click()}
              >
                <FaCamera />
              </button>
              <input
                id="dpInput"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
            <p className={`mt-6 text-[10px] font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
              Click to update photo
            </p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className={`${theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2.5rem] p-8 sm:p-10 shadow-2xl border`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Full Name */}
                <div className="space-y-2">
                  <label className={`text-[10px] font-bold uppercase tracking-wider ml-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={user.first_name || ""}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={`w-full px-6 py-4 rounded-2xl text-sm font-bold transition-all duration-300 border-2 ${
                      theme === 'dark'
                        ? 'bg-slate-900/50 border-slate-700 text-slate-100 focus:border-blue-500 focus:bg-slate-900'
                        : 'bg-slate-50 border-slate-100 text-slate-900 focus:border-blue-500 focus:bg-white'
                    } outline-none`}
                  />
                  {errors?.first_name && <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest ml-1 mt-1">{errors.first_name[0]}</p>}
                </div>

                {/* Email (Disabled) */}
                <div className="space-y-2">
                  <label className={`text-[10px] font-bold uppercase tracking-wider ml-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user.email || ""}
                    disabled
                    className={`w-full px-6 py-4 rounded-2xl text-sm font-bold border-2 ${
                      theme === 'dark'
                        ? 'bg-slate-800 border-slate-700/50 text-slate-500'
                        : 'bg-slate-100 border-slate-200 text-slate-400'
                    } cursor-not-allowed`}
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label className={`text-[10px] font-bold uppercase tracking-wider ml-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={user.phone || ""}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className={`w-full px-6 py-4 rounded-2xl text-sm font-bold transition-all duration-300 border-2 ${
                      theme === 'dark'
                        ? 'bg-slate-900/50 border-slate-700 text-slate-100 focus:border-blue-500 focus:bg-slate-900'
                        : 'bg-slate-50 border-slate-100 text-slate-900 focus:border-blue-500 focus:bg-white'
                    } outline-none`}
                  />
                  {errors?.phone && <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest ml-1 mt-1">{errors.phone[0]}</p>}
                </div>

                {/* About */}
                <div className="md:col-span-2 space-y-2">
                  <label className={`text-[10px] font-bold uppercase tracking-wider ml-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    About You
                  </label>
                  <textarea
                    name="about"
                    value={user.about || ""}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Write a brief bio..."
                    className={`w-full px-6 py-4 rounded-3xl text-sm font-bold transition-all duration-300 border-2 ${
                      theme === 'dark'
                        ? 'bg-slate-900/50 border-slate-700 text-slate-100 focus:border-blue-500 focus:bg-slate-900'
                        : 'bg-slate-50 border-slate-100 text-slate-900 focus:border-blue-500 focus:bg-white'
                    } outline-none resize-none`}
                  />
                </div>
              </div>
            </div>

            {/* Notifications Toggle Section */}
            <div className={`${theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2.5rem] p-8 sm:p-10 shadow-2xl border`}>
              <h3 className={`text-lg font-bold tracking-tight mb-8 ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>Preferences</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { name: 'push_notification', label: 'Push Notifications', icon: '🔔' },
                  { name: 'email_notification', label: 'Email Notifications', icon: '✉️' }
                ].map((item) => (
                  <label 
                    key={item.name}
                    className={`flex items-center justify-between p-6 rounded-3xl border-2 transition-all cursor-pointer ${
                      user[item.name] 
                        ? 'border-blue-500 bg-blue-500/5 shadow-inner' 
                        : theme === 'dark' ? 'border-slate-700 bg-slate-900/30' : 'border-slate-100 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{item.icon}</span>
                      <span className={`text-xs font-bold uppercase tracking-wider ${user[item.name] ? 'text-blue-500' : theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {item.label}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        name={item.name}
                        checked={!!user[item.name]}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors duration-300 ${user[item.name] ? 'bg-blue-600' : 'bg-slate-400'}`} />
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${user[item.name] ? 'translate-x-6' : ''}`} />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Final Actions */}
            <div className="space-y-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 rounded-[2.5rem] bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm uppercase tracking-wider shadow-[0_20px_50px_rgba(37,_99,_235,_0.3)] hover:shadow-[0_20px_50px_rgba(37,_99,_235,_0.5)] active:scale-[0.98] transition-all duration-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : "Save Changes"}
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {user?.expired_at && (
                  <button
                    type="button"
                    onClick={unsubscribe}
                    disabled={loading}
                    className={`group relative overflow-hidden py-4 rounded-2xl font-bold text-[10px] uppercase tracking-wider transition-all duration-300 border-2 ${
                      theme === 'dark' ? 'border-slate-700 text-slate-400 hover:border-red-500/50' : 'border-slate-100 text-slate-500 hover:border-red-200'
                    }`}
                  >
                    <span className="relative z-10 group-hover:text-red-500 transition-colors duration-300">Unsubscribe</span>
                    <div className="absolute inset-0 bg-red-500/5 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={deleteAccount}
                  disabled={loading}
                  className={`group relative overflow-hidden py-4 rounded-2xl font-bold text-[10px] uppercase tracking-wider transition-all duration-300 border-2 ${
                    theme === 'dark' ? 'border-slate-700 text-slate-400 hover:border-red-500/50' : 'border-slate-100 text-slate-500 hover:border-red-200'
                  } ${!user?.expired_at ? 'sm:col-span-2' : ''}`}
                >
                  <span className="relative z-10 group-hover:text-red-500 transition-colors duration-300">Delete Account</span>
                  <div className="absolute inset-0 bg-red-500/5 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </button>
              </div>

              {errors?.form && (
                <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-bold uppercase tracking-widest text-center">
                  {errors.form[0]}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
